'use client';
import { useEffect, useRef } from 'react';

const benefits = [
  { icon: '🎨', title: 'Design Faster', desc: 'Use ready-made Canva templates, fonts, SVGs, graphics and UI resources — start any creative project in minutes.', gradient: 'from-pink-500/10 to-purple-500/5', hover: 'hover:border-pink-500/30' },
  { icon: '📚', title: 'Publish Faster', desc: 'Access KDP interiors, planners, journals, worksheets and publishing resources ready to upload to Amazon KDP.', gradient: 'from-violet-500/10 to-indigo-500/5', hover: 'hover:border-violet-500/30' },
  { icon: '💻', title: 'Build Faster', desc: 'Start with website templates, UI kits, games and 71 complete application source-code projects with admin panels.', gradient: 'from-blue-500/10 to-cyan-500/5', hover: 'hover:border-blue-500/30' },
  { icon: '📱', title: 'Create Content Faster', desc: 'Use 100K+ stock photos, 300K+ quote libraries, YouTube resources and social-media assets.', gradient: 'from-emerald-500/10 to-teal-500/5', hover: 'hover:border-emerald-500/30' },
  { icon: '📊', title: 'Run Your Business', desc: 'Use spreadsheets, resumes, HR toolkits, Notion templates and business tools to stay organized and professional.', gradient: 'from-amber-500/10 to-orange-500/5', hover: 'hover:border-amber-500/30' },
  { icon: '🛍️', title: 'Build Digital Products', desc: 'Start with existing resources rather than beginning every project from a blank page — launch new income streams faster.', gradient: 'from-rose-500/10 to-pink-500/5', hover: 'hover:border-rose-500/30' },
];

export default function BenefitsSection() {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-hidden');
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    itemRefs.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="whats-included"
      className="relative py-20 lg:py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #080B14 0%, #0B1020 100%)' }}
      aria-labelledby="benefits-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="text-center mb-14 reveal-hidden"
          ref={(el) => { itemRefs.current[0] = el; }}
        >
          <h2 id="benefits-heading" className="section-heading mb-4" style={{ color: 'var(--foreground)' }}>
            What Would Take Months to Collect{' '}
            <span className="text-gradient">Is Already Here.</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            43 professional-grade collections, organized so you can find what you need and start creating immediately.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {benefits.map((b, i) => (
            <div
              key={i}
              ref={(el) => { itemRefs.current[i + 1] = el; }}
              className={`reveal-hidden relative rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1 cursor-default bg-gradient-to-br ${b.gradient} ${b.hover}`}
              style={{ borderColor: 'rgba(30,41,59,0.6)', transitionDelay: `${i * 80}ms` }}
            >
              <div className="text-3xl mb-4" aria-hidden="true">{b.icon}</div>
              <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--foreground)' }}>{b.title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
